import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  MonitorSmartphone,
  RefreshCw,
  Loader2,
  LogOut,
  CheckCircle2,
  XCircle,
  Globe,
  Clock,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return String(iso);
  }
};

const parseUA = (ua) => {
  if (!ua) return "Perangkat tidak dikenal";
  let browser = "Browser";
  if (/edg/i.test(ua)) browser = "Edge";
  else if (/opr|opera/i.test(ua)) browser = "Opera";
  else if (/chrome|crios/i.test(ua)) browser = "Chrome";
  else if (/firefox|fxios/i.test(ua)) browser = "Firefox";
  else if (/safari/i.test(ua)) browser = "Safari";
  let os = "";
  if (/windows/i.test(ua)) os = "Windows";
  else if (/android/i.test(ua)) os = "Android";
  else if (/iphone|ipad|ios/i.test(ua)) os = "iOS";
  else if (/mac os|macintosh/i.test(ua)) os = "macOS";
  else if (/linux/i.test(ua)) os = "Linux";
  return os ? `${browser} • ${os}` : browser;
};

const decodeJti = (token) => {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return json.jti || null;
  } catch (e) {
    return null;
  }
};

export default function LoginHistoryPanel() {
  const { authAxios, user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState("");
  const isSuper = user?.role === "superadmin";
  const currentJti = decodeJti(token);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get("/admin/login-history?limit=100");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Gagal memuat riwayat login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (jti) => {
    if (!jti) return;
    setRevoking(jti);
    try {
      await authAxios.post(`/admin/login-history/${jti}/revoke`);
      toast.success("Sesi berhasil dikeluarkan");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Gagal mengeluarkan sesi";
      toast.error(detail);
    } finally {
      setRevoking("");
    }
  };

  return (
    <div className="max-w-5xl" data-testid="login-history-panel">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-800 text-white flex items-center justify-center shrink-0">
            <MonitorSmartphone className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl text-slate-900">Riwayat Login</h1>
            <p className="text-sm text-slate-500">
              {isSuper
                ? "Pantau aktivitas login akun admin & keluarkan perangkat yang mencurigakan."
                : "Aktivitas login untuk akun Anda."}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm shrink-0"
          data-testid="login-history-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Segarkan
        </button>
      </div>

      {isSuper && (
        <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-900 flex gap-2">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Aktivitas login <b>superadmin tidak dicatat</b> demi privasi. Daftar ini hanya berisi akun role <b>admin</b>.
          </div>
        </div>
      )}

      <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Memuat riwayat...
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm" data-testid="login-history-empty">
            Belum ada aktivitas login yang tercatat.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="login-history-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-100 bg-slate-50/60">
                  <th className="px-4 py-3 font-semibold">Waktu</th>
                  {isSuper && <th className="px-4 py-3 font-semibold">Akun</th>}
                  <th className="px-4 py-3 font-semibold">IP</th>
                  <th className="px-4 py-3 font-semibold">Perangkat</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const isCurrent = it.jti && it.jti === currentJti;
                  const canRevoke =
                    it.status === "success" && it.jti && !it.revoked && !isCurrent;
                  return (
                    <tr
                      key={it.id}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50"
                      data-testid={`login-history-row-${it.id}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {fmtDateTime(it.created_at)}
                        </span>
                      </td>
                      {isSuper && (
                        <td className="px-4 py-3 text-slate-700">{it.user_email || "—"}</td>
                      )}
                      <td className="px-4 py-3 text-slate-600">
                        <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                          <Globe className="w-3.5 h-3.5 text-slate-400" />
                          {it.ip || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{parseUA(it.user_agent)}</td>
                      <td className="px-4 py-3">
                        {it.status === "success" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                            <CheckCircle2 className="w-3 h-3" /> Berhasil
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-medium">
                            <XCircle className="w-3 h-3" /> Gagal
                          </span>
                        )}
                        {it.revoked && (
                          <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs">
                            Dikeluarkan
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isCurrent ? (
                          <span className="text-xs text-blue-700 font-medium">Sesi ini</span>
                        ) : canRevoke ? (
                          <button
                            onClick={() => revoke(it.jti)}
                            disabled={revoking === it.jti}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs font-medium disabled:opacity-60"
                            data-testid={`login-history-revoke-${it.id}`}
                          >
                            {revoking === it.jti ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <LogOut className="w-3.5 h-3.5" />
                            )}
                            Keluarkan
                          </button>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
